import { DND_IDENTIFIER, SearchField, useAppUser, useAvailableMilestoneTypesQuery } from '@bechtel/client-common';
import { WeldMilestoneType } from '@bechtel/client-common/src/types';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Checkbox,
  Icon,
  MultiSelectCheckbox,
  Text,
  TextFieldAppearance,
  TextFieldSize,
} from '@bechtel/gds';
import { MilestoneType } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { get, orderBy } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import {
  CheckboxContainer,
  HeaderContainer,
  MilestoneTypeDropdownContainer,
  SearchBoxContainer,
  UnplanButtonContainer,
} from './styled';
import { PlanningWeeksFilter } from './types';

interface PlanningWeeksHeaderProps {
  filter: PlanningWeeksFilter;
  onFilterChange: (filter: PlanningWeeksFilter) => void;
  onSelectAll: (selected: boolean) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  disabled?: boolean;
  selectedMilestoneDraggableIds: string[];
  unplanMilestones: () => void;
  canPlanMilestones: boolean;
}

export const PlanningWeeksHeader: React.FC<PlanningWeeksHeaderProps> = ({
  filter,
  onFilterChange,
  onSelectAll,
  isAllSelected,
  isIndeterminate,
  disabled = false,
  selectedMilestoneDraggableIds,
  unplanMilestones,
  canPlanMilestones,
}) => {
  const { user } = useAppUser();
  const { data } = useAvailableMilestoneTypesQuery(user?.project?.id);

  const isUnplanMilestonesDisabled =
    !selectedMilestoneDraggableIds ||
    selectedMilestoneDraggableIds.length <= 0 ||
    (selectedMilestoneDraggableIds &&
      selectedMilestoneDraggableIds.some(
        (id) => id.includes(DND_IDENTIFIER.Unplanned) && !id.includes(DND_IDENTIFIER.Expired)
      ));

  const milestoneTypes: MilestoneType[] = orderBy(
    get(data, 'projectConfigById.availableMilestoneTypes', []).filter(({ name }) => {
      return name?.toUpperCase() !== WeldMilestoneType.INSTALL && name?.toUpperCase() !== WeldMilestoneType.FITUP;
    }),
    [(item) => item?.commodityTypes?.[0]?.name?.toLowerCase() || '', (item) => item?.name?.toLowerCase() || ''],
    ['asc', 'asc']
  );
  const selectedMilestoneTypes = useMemo(() => {
    if (!filter.milestoneTypeIds || filter.milestoneTypeIds.length === 0) {
      return [];
    }
    return milestoneTypes.filter((type) => filter.milestoneTypeIds.includes(type.id));
  }, [filter.milestoneTypeIds, milestoneTypes]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSelectAll(event.target.checked);
    },
    [onSelectAll]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e?.target?.value || '';
      onFilterChange({
        ...filter,
        searchKeyword: typeof value === 'string' ? value : '',
      });
    },
    [filter, onFilterChange]
  );

  const handleMilestoneTypeChange = useCallback(
    (selectedItems: any[]) => {
      // If all items are selected or none selected, clear the filter (show all)
      if (!selectedItems || selectedItems.length === 0 || selectedItems.length === milestoneTypes.length) {
        onFilterChange({
          ...filter,
          milestoneTypeIds: [],
        });
      } else {
        const selectedIds = selectedItems.map((item) => item.id);
        onFilterChange({
          ...filter,
          milestoneTypeIds: selectedIds,
        });
      }
    },
    [filter, onFilterChange, milestoneTypes.length]
  );

  // Custom item component for dropdown
  const MilestoneTypeItem = useCallback(({ item }: { item: any }) => {
    if (!item) return null;
    const displayText = item.commodityTypes?.[0]?.name ? `${item.name} (${item.commodityTypes?.[0]?.name})` : item.name;
    return (
      <Text size="400" color="grey.1000">
        {displayText}
      </Text>
    );
  }, []);

  // Custom selected component for display - shows count
  const SelectedMilestoneType = useCallback(({ selectedItemCount, totalItemCount }: any) => {
    return (
      <Box pl={{ sm: '500' }}>
        <Text size="400" color="grey.1000" style={{ whiteSpace: 'nowrap' }}>
          {selectedItemCount === totalItemCount || selectedItemCount === 0
            ? t`All`
            : selectedItemCount === 1
              ? t`${selectedItemCount} Type`
              : t`${selectedItemCount} Types`}
        </Text>
      </Box>
    );
  }, []);

  return (
    <HeaderContainer>
      <CheckboxContainer>
        <Checkbox
          id="select-all-weeks"
          type="checkbox"
          checked={isAllSelected}
          ref={(input: HTMLInputElement | null) => {
            if (input) {
              input.indeterminate = isIndeterminate;
            }
          }}
          onChange={handleCheckboxChange}
          disabled={disabled}
          aria-label="Select all planned milestones"
        />
      </CheckboxContainer>
      <SearchBoxContainer>
        <SearchField
          placeholder="Search by commodity tag"
          value={filter.searchKeyword}
          onChange={handleSearchChange}
          disabled={disabled}
        />
      </SearchBoxContainer>
      <MilestoneTypeDropdownContainer>
        <MultiSelectCheckbox
          items={milestoneTypes}
          initialSelectedItems={selectedMilestoneTypes}
          itemComponent={MilestoneTypeItem}
          selectedComponent={SelectedMilestoneType}
          size={TextFieldSize.MEDIUM}
          appearance={TextFieldAppearance.NORMAL}
          displayKeyField="name"
          placeholder={t`All`}
          showSelectAll={true}
          confirmSelection={true}
          onSelectedItemsChange={handleMilestoneTypeChange}
          disabled={disabled}
        />
      </MilestoneTypeDropdownContainer>
      {canPlanMilestones && (
        <UnplanButtonContainer>
          <Button
            size={ButtonSize.SMALL}
            appearance={ButtonAppearance.SECONDARY}
            disabled={isUnplanMilestonesDisabled}
            onClick={unplanMilestones}
            data-testid="planned-unplan"
          >
            <Box mr={{ sm: '300' }}>
              <Icon icon="undo" fillColor="blue.800" />
            </Box>
            <Text size="300" color="grey.1000">
              <Trans>Unplan Selected Milestones</Trans>
            </Text>
          </Button>
        </UnplanButtonContainer>
      )}
    </HeaderContainer>
  );
};
